/**
 * VoiceProvider — app-scoped browser phone + cross-device call state.
 *
 * Why app-scoped: the Twilio `Device` used to live inside the Call History
 * dialer modal and was destroyed when it closed, so "Continue here" could
 * only work on that one page. This provider owns:
 *
 *   - the Twilio Device, the live browser call, mute state and status.
 *     The Device is created and **registered on mount**: registration is
 *     what makes this browser reachable, so a Device that only appears
 *     when the dialer modal opens can never take an incoming call;
 *   - this browser's identity as a REWS device (`POST /push/register`,
 *     platform "web") so the iOS transfer sheet can list it;
 *   - the server-side active-call registry (`call.active` / `call:handoff`
 *     WebSocket frames + `GET /voice/calls/active`) that powers the
 *     "Call with Pat on iPhone — Continue here" banner;
 *   - hand-off invites aimed at this browser;
 *   - post-call thank-you follow-ups queued by the server.
 *
 * Every call is a Twilio Conference on the backend, so joining a call
 * that is live on another device is just `connect({ ConferenceName })`.
 *
 * Microphone permission is deliberately *not* part of registration. The
 * SDK only needs the microphone to connect or accept a call, so asking
 * on mount would prompt every admin on every page load. `prepare()`
 * still does both, for the dialer's "Enable microphone" button.
 */

import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
    type ReactNode,
} from 'react';
import { Device, type Call as TwilioCall } from '@twilio/voice-sdk';
import { apiClient } from '@/shared/api';
import { useSmsWebSocket, type WsEvent } from '@/hooks';
import { notify } from '@/utils/notify';
import { delayForAttempt, retryAsync } from '@/utils/backoff';
import { BANNED_CALLER_RING_ANSWER, banSummary } from '@/utils/contactBan';
import { contactBanApi } from '@/features/contacts/api/contactsApi';

// ==================== Types ====================

export type VoiceStatus = 'idle' | 'ready' | 'connecting' | 'ringing' | 'in-call' | 'ended';
export type MicPermission = 'prompt' | 'requesting' | 'granted' | 'denied';

/**
 * Whether Twilio can route an inbound call to this browser.
 *
 *   registering   token fetched / Device registering (also the initial state)
 *   registered    reachable — an inbound call rings here
 *   offline       registration failed or was lost; retrying on a backoff
 *   unconfigured  the server cannot issue a token (Twilio voice not set
 *                 up); retried only by hand, so a deployment without
 *                 voice is not polled forever
 */
export type RegistrationState = 'registering' | 'registered' | 'offline' | 'unconfigured';

export interface VoiceRegistration {
    state: RegistrationState;
    /** The Twilio client identity this browser is registered as. */
    identity: string | null;
    error: string | null;
    /** Retries so far since the last success. */
    attempt: number;
    /** Epoch ms of the next automatic retry, when one is scheduled. */
    nextRetryAt: number | null;
}

/** Row from `rews_active_calls` — the call currently live somewhere. */
export interface ActiveCall {
    conference_name: string;
    phone: string | null;
    display_name: string | null;
    direction: 'inbound' | 'outbound' | null;
    device_id: string | null;
    device_kind: 'ios' | 'android' | 'web' | 'cell' | null;
    device_name: string | null;
    started_at: string | null;
    connected_at: string | null;
}

/** A call ringing this browser right now. */
export interface IncomingCall {
    phone: string | null;
    displayName: string | null;
    /** A banned contact is calling. */
    banned: boolean;
    /** False under "Ring, but can't answer": the call goes to voicemail. */
    answerable: boolean;
}

export interface HandoffInvite {
    conference_name: string;
    phone: string | null;
    display_name: string | null;
    to_device_id: string;
    from_device_name: string | null;
}

export interface CallFollowup {
    id: number;
    call_sid: string;
    phone: string;
    email: string | null;
    display_name: string;
    scheduled_for: string | null;
    email_status: string;
    sms_status: string;
    cancelled_at: string | null;
}

interface VoiceContextValue {
    // Browser phone
    status: VoiceStatus;
    micPermission: MicPermission;
    initializing: boolean;
    error: string | null;
    isReady: boolean;
    isCalling: boolean;
    callTarget: string | null;
    callLabel: string | null;
    muted: boolean;
    /** True once Twilio can route an inbound call to this browser. */
    isRegistered: boolean;
    registration: VoiceRegistration;
    /** Try to register now, resetting the backoff. */
    retryRegistration: () => void;
    prepare: () => Promise<void>;
    callNumber: (phone: string) => Promise<void>;
    joinConference: (conferenceName: string, label?: string | null) => Promise<void>;
    hangUp: () => void;
    toggleMute: () => void;
    // Inbound
    incomingCall: IncomingCall | null;
    acceptIncoming: () => Promise<void>;
    rejectIncoming: () => void;
    // Cross-device
    deviceId: string;
    activeCall: ActiveCall | null;
    /** True when the registry says the call is live on some other device. */
    isActiveElsewhere: boolean;
    continueHere: () => Promise<void>;
    /** Hang up the call wherever it is live (another device, or a phantom
     *  registry row left by a lost callback). */
    endActiveCall: () => Promise<void>;
    invite: HandoffInvite | null;
    acceptInvite: () => Promise<void>;
    dismissInvite: () => void;
    sendToDevice: (toDeviceId: string) => Promise<void>;
    movedTo: string | null;
    clearMovedTo: () => void;
    // Follow-ups
    followups: CallFollowup[];
    cancelFollowup: (id: number) => Promise<void>;
    sendFollowupNow: (id: number) => Promise<void>;
}

// ==================== Constants ====================

const WEB_DEVICE_ID_KEY = 'rews.web.device_id';
const DEVICE_KIND_WEB = 'web';
const REGISTRATION_TIMEOUT_MS = 30000;
// A short blip recovers in seconds; a dead backend is polled every 5 min.
const REGISTRATION_RETRY_DELAYS_MS = [5_000, 15_000, 60_000, 300_000] as const;
const TOKEN_REFRESH_RETRY_DELAYS_MS = [2_000, 5_000, 15_000] as const;
const REGISTRATION_TOAST_ID = 'voice-registration';
// SDK errors after which the only cure is a fresh token and a new
// registration: invalid/expired access token (20101/20104), auth failed
// (31202), signaling connection lost (31005/31009).
const REREGISTER_ERROR_CODES = new Set([20101, 20104, 31202, 31005, 31009]);
const HTTP_SERVICE_UNAVAILABLE = 503;

const INITIAL_REGISTRATION: VoiceRegistration = {
    state: 'registering', identity: null, error: null, attempt: 0, nextRetryAt: null,
};
const DEVICE_OPTIONS = {
    codecPreferences: ['opus', 'pcmu'],
    closeProtection: true,
    edge: ['ashburn', 'umatilla'],
    enableImprovedSignalingErrorPrecision: true,
    maxCallSignalingTimeoutMs: 30000,
} as unknown as ConstructorParameters<typeof Device>[1];

const VoiceContext = createContext<VoiceContextValue | null>(null);

// ==================== Helpers ====================

const getOrCreateWebDeviceId = (): string => {
    try {
        const existing = localStorage.getItem(WEB_DEVICE_ID_KEY);
        if (existing) return existing;
        const fresh = (crypto.randomUUID ? crypto.randomUUID() : `web-${Date.now()}`).slice(0, 64);
        localStorage.setItem(WEB_DEVICE_ID_KEY, fresh);
        return fresh;
    } catch {
        return `web-${Date.now()}`;
    }
};

const describeBrowser = (): string => {
    const ua = navigator.userAgent;
    const browser = /Edg\//.test(ua) ? 'Edge'
        : /Chrome\//.test(ua) ? 'Chrome'
        : /Safari\//.test(ua) ? 'Safari'
        : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
    const os = /Mac OS/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
    return os ? `${browser} on ${os}` : browser;
};

interface TwilioLikeError {
    code?: string | number;
    name?: string;
    message?: string;
    explanation?: string;
    causes?: unknown[];
    twilioError?: TwilioLikeError;
    response?: { data?: { detail?: string } };
}

const asError = (err: unknown): TwilioLikeError => (typeof err === 'object' && err ? err as TwilioLikeError : {});

const errorCode = (raw: unknown): number | null => {
    const err = asError(raw);
    const code = Number((err.twilioError || err).code);
    return Number.isFinite(code) ? code : null;
};

const isServiceUnavailable = (raw: unknown): boolean =>
    (raw as { response?: { status?: number } })?.response?.status === HTTP_SERVICE_UNAVAILABLE;

const getVoiceErrorMessage = (raw: unknown, fallback: string): string => {
    const err = asError(raw);
    const twilioError = err.twilioError || err;
    const code = twilioError.code || err.code;
    const name = twilioError.name || err.name;
    const message = twilioError.message || err.message;
    const explanation = twilioError.explanation;
    const causes = Array.isArray(twilioError.causes) ? twilioError.causes.filter(Boolean).join(' ') : '';
    if (code || name || message) {
        const detail = [name, code ? `code ${code}` : null].filter(Boolean).join(' - ');
        const body = explanation || causes || message;
        return [detail, body].filter(Boolean).join(': ');
    }
    return fallback;
};

const waitForRegistration = (device: Device): Promise<void> =>
    new Promise((resolve, reject) => {
        if (device.state === Device.State.Registered) { resolve(); return; }
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error('Timed out waiting for Twilio voice registration.'));
        }, REGISTRATION_TIMEOUT_MS);
        const onRegistered = () => { cleanup(); resolve(); };
        const onError = (e: unknown) => { cleanup(); reject(e); };
        const cleanup = () => {
            clearTimeout(timer);
            device.off('registered', onRegistered);
            device.off('error', onError);
        };
        device.on('registered', onRegistered);
        device.on('error', onError);
        device.register().catch(onError);
    });

/**
 * Who is calling, from the parameters Twilio delivers with the invite.
 *
 * The backend dials `client:admin_user_N?CallerPhone=…&CallerName=…`,
 * which arrive as custom parameters. `parameters.From` is only the
 * Twilio number the conference leg was placed from, so it is the last
 * resort rather than the first.
 */
const describeIncoming = (call: TwilioCall): IncomingCall => {
    const custom = call.customParameters;
    const phone = custom?.get('CallerPhone') || call.parameters?.From || null;
    const name = custom?.get('CallerName') || null;
    // Set by the backend when the caller is a banned contact.
    const banned = custom?.get('CallerBanned') === '1';
    const answerable = !banned || custom?.get('BannedCallerMode') === BANNED_CALLER_RING_ANSWER;
    return { phone, displayName: name, banned, answerable };
};

const parseActiveCall = (data: Record<string, unknown>): ActiveCall | null => {
    if (data.cleared || !data.conference_name) return null;
    return {
        conference_name: String(data.conference_name),
        phone: (data.phone as string) || null,
        display_name: (data.display_name as string) || null,
        direction: (data.direction as ActiveCall['direction']) || null,
        device_id: (data.device_id as string) || null,
        device_kind: (data.device_kind as ActiveCall['device_kind']) || null,
        device_name: (data.device_name as string) || null,
        started_at: (data.started_at as string) || null,
        connected_at: (data.connected_at as string) || null,
    };
};

const parseFollowup = (data: Record<string, unknown>): CallFollowup | null => {
    if (typeof data.id !== 'number') return null;
    return {
        id: data.id,
        call_sid: String(data.call_sid || ''),
        phone: String(data.phone || ''),
        email: (data.email as string) || null,
        display_name: String(data.display_name || data.phone || ''),
        scheduled_for: (data.scheduled_for as string) || null,
        email_status: String(data.email_status || ''),
        sms_status: String(data.sms_status || ''),
        cancelled_at: (data.cancelled_at as string) || null,
    };
};

const isFollowupOpen = (f: CallFollowup) =>
    !f.cancelled_at && (f.email_status === 'pending' || f.sms_status === 'pending');

// ==================== Provider ====================

export function VoiceProvider({ children }: { children: ReactNode }) {
    const deviceId = useMemo(() => getOrCreateWebDeviceId(), []);
    const deviceName = useMemo(() => describeBrowser(), []);

    const deviceRef = useRef<Device | null>(null);
    const callRef = useRef<TwilioCall | null>(null);
    const incomingRef = useRef<TwilioCall | null>(null);
    // Mirrors `deviceRef.current !== null` as state so render can read it.
    const [hasDevice, setHasDevice] = useState(false);
    const [registration, setRegistration] = useState<VoiceRegistration>(INITIAL_REGISTRATION);
    const isRegistered = registration.state === 'registered';
    // Registration plumbing. Refs, not state: timers and SDK listeners
    // must see the latest values without re-subscribing.
    const pendingRef = useRef<Promise<Device | null> | null>(null);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const attemptRef = useRef(0);
    const unmountedRef = useRef(false);
    const destroyingRef = useRef(false);
    const toastShownRef = useRef(false);
    const reregisterAfterCallRef = useRef(false);
    // Latest callbacks for timers and SDK listeners, which outlive renders.
    const ensureDeviceRef = useRef<() => Promise<Device | null>>(async () => null);
    const recoverAfterCallRef = useRef<() => void>(() => {});
    const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
    const [status, setStatus] = useState<VoiceStatus>('idle');
    const [micPermission, setMicPermission] = useState<MicPermission>('prompt');
    const [initializing, setInitializing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [callTarget, setCallTarget] = useState<string | null>(null);
    const [callLabel, setCallLabel] = useState<string | null>(null);
    const [muted, setMuted] = useState(false);

    const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
    const [invite, setInvite] = useState<HandoffInvite | null>(null);
    const [movedTo, setMovedTo] = useState<string | null>(null);
    const [followups, setFollowups] = useState<CallFollowup[]>([]);

    // ── This browser as a REWS device (best-effort) ──
    // Registered on mount so the iOS transfer sheet can list it, and again
    // on every successful Twilio registration and on each return to the
    // tab: the server rings the admin whose device was seen most recently,
    // so a fresh `last_seen_at` is what keeps inbound calls coming here.
    const registerWebDevice = useCallback(() => {
        apiClient.post('/push/register', {
            platform: DEVICE_KIND_WEB,
            device_id: deviceId,
            device_name: deviceName,
            model_name: deviceName,
            system_name: 'Web',
            app_version: 'web',
        }).catch(() => { /* never block the admin on this */ });
    }, [deviceId, deviceName]);

    useEffect(() => { registerWebDevice(); }, [registerWebDevice]);

    // ── Initial registry + pending follow-ups ──
    useEffect(() => {
        let cancelled = false;
        apiClient.get('/voice/calls/active')
            .then(res => { if (!cancelled) setActiveCall(res.data?.data ? parseActiveCall(res.data.data) : null); })
            .catch(() => { /* ignore */ });
        apiClient.get('/call-followup/pending')
            .then(res => {
                if (cancelled) return;
                const rows = (res.data?.data ?? []) as Record<string, unknown>[];
                setFollowups(rows.map(parseFollowup).filter((f): f is CallFollowup => Boolean(f)));
            })
            .catch(() => { /* ignore */ });
        return () => { cancelled = true; };
    }, []);

    // ── Twilio device ──
    const fetchToken = async (): Promise<string> => {
        const res = await apiClient.get('/voice/token');
        const token = res.data?.data?.token;
        if (!token) throw new Error('Could not fetch Twilio voice token.');
        return token;
    };

    const clearIncoming = useCallback(() => {
        incomingRef.current = null;
        setIncomingCall(null);
    }, []);

    const clearRetryTimer = () => {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
    };

    /** Drop the Device without firing our own `unregistered` handling. */
    const teardownDevice = useCallback(() => {
        destroyingRef.current = true;
        try { deviceRef.current?.destroy(); } finally { destroyingRef.current = false; }
        deviceRef.current = null;
        setHasDevice(false);
    }, []);

    /** Schedule the next registration attempt on the backoff table. */
    const scheduleRetry = useCallback(() => {
        if (unmountedRef.current || retryTimerRef.current) return;
        const delay = delayForAttempt(REGISTRATION_RETRY_DELAYS_MS, attemptRef.current);
        attemptRef.current += 1;
        setRegistration(prev => ({ ...prev, attempt: attemptRef.current, nextRetryAt: Date.now() + delay }));
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            void ensureDeviceRef.current();
        }, delay);
    }, []);

    const markOffline = useCallback((message: string) => {
        setRegistration(prev => ({ ...prev, state: 'offline', error: message, nextRetryAt: null }));
    }, []);

    /** Registration was lost or failed: tear down, tell the operator once, retry. */
    const loseRegistration = useCallback((message: string) => {
        teardownDevice();
        markOffline(message);
        if (!toastShownRef.current) {
            toastShownRef.current = true;
            notify.warning('Browser phone is offline', {
                id: REGISTRATION_TOAST_ID,
                description: 'Incoming calls only ring your cell until it reconnects. Retrying…',
            });
        }
        scheduleRetry();
    }, [markOffline, scheduleRetry, teardownDevice]);

    /**
     * Create and register the Device. No microphone involved — the SDK
     * only needs it to connect or accept, and registering is what makes
     * this browser reachable for inbound calls.
     *
     * One attempt at a time: concurrent callers (the mount effect under
     * StrictMode, a retry timer, `prepare()`) share the in-flight promise.
     */
    const ensureDevice = useCallback((): Promise<Device | null> => {
        if (deviceRef.current?.state === Device.State.Registered) return Promise.resolve(deviceRef.current);
        if (pendingRef.current) return pendingRef.current;

        const run = async (): Promise<Device | null> => {
            clearRetryTimer();
            setInitializing(true);
            setRegistration(prev => ({ ...prev, state: 'registering', nextRetryAt: null }));
            try {
                const token = await fetchToken();
                const device = new Device(token, DEVICE_OPTIONS);
                device.on('error', (twilioError: unknown) => {
                    setError(getVoiceErrorMessage(twilioError, 'Could not connect to Twilio Voice.'));
                    setStatus(prev => (prev === 'in-call' ? prev : 'idle'));
                    const code = errorCode(twilioError);
                    if (code !== null && REREGISTER_ERROR_CODES.has(code) && !callRef.current && deviceRef.current === device) {
                        loseRegistration(getVoiceErrorMessage(twilioError, 'Lost the connection to Twilio.'));
                    }
                });
                device.on('tokenWillExpire', () => {
                    retryAsync(fetchToken, TOKEN_REFRESH_RETRY_DELAYS_MS)
                        .then(fresh => device.updateToken(fresh))
                        .catch(() => {
                            if (deviceRef.current !== device) return;
                            if (callRef.current) { reregisterAfterCallRef.current = true; return; }
                            loseRegistration('Could not renew the voice session.');
                        });
                });
                device.on('registered', () => {
                    if (deviceRef.current !== device) return;
                    setRegistration({ state: 'registered', identity: device.identity, error: null, attempt: 0, nextRetryAt: null });
                });
                device.on('unregistered', () => {
                    if (destroyingRef.current || deviceRef.current !== device) return;
                    if (callRef.current) {
                        // Never drop a live call over a lost registration —
                        // re-register once it ends.
                        reregisterAfterCallRef.current = true;
                        markOffline('Lost the connection to Twilio.');
                        return;
                    }
                    if (device.state === Device.State.Unregistered) {
                        setRegistration(prev => ({ ...prev, state: 'registering' }));
                        waitForRegistration(device).catch((err: unknown) => {
                            loseRegistration(getVoiceErrorMessage(err, 'Could not re-register with Twilio.'));
                        });
                    }
                });
                device.on('incoming', (call: TwilioCall) => {
                    incomingRef.current = call;
                    setIncomingCall(describeIncoming(call));
                    // Twilio cancels the invite when another of our devices
                    // answers first, or when the caller gives up.
                    call.on('cancel', clearIncoming);
                    call.on('disconnect', clearIncoming);
                    call.on('reject', clearIncoming);
                });
                deviceRef.current = device;
                setHasDevice(true);
                await waitForRegistration(device);
                if (unmountedRef.current) { teardownDevice(); return null; }
                attemptRef.current = 0;
                setRegistration({ state: 'registered', identity: device.identity, error: null, attempt: 0, nextRetryAt: null });
                setStatus(prev => (prev === 'idle' ? 'ready' : prev));
                registerWebDevice();
                if (toastShownRef.current) {
                    toastShownRef.current = false;
                    notify.success('Browser phone connected', { id: REGISTRATION_TOAST_ID });
                }
                return device;
            } catch (err: unknown) {
                const e = asError(err);
                const message = e.response?.data?.detail || e.message || 'Failed to start browser calling.';
                setStatus('idle');
                setError(message);
                if (unmountedRef.current) { teardownDevice(); return null; }
                if (isServiceUnavailable(err)) {
                    // Voice is not set up on the server; polling would only
                    // produce noise. The header pill says what is missing.
                    teardownDevice();
                    setRegistration({ state: 'unconfigured', identity: null, error: message, attempt: 0, nextRetryAt: null });
                } else {
                    loseRegistration(message);
                }
                return null;
            } finally {
                setInitializing(false);
            }
        };

        const pending = run().finally(() => { pendingRef.current = null; });
        pendingRef.current = pending;
        return pending;
    }, [clearIncoming, loseRegistration, markOffline, registerWebDevice, teardownDevice]);

    const retryRegistration = useCallback(() => {
        clearRetryTimer();
        attemptRef.current = 0;
        void ensureDevice();
    }, [ensureDevice]);

    useEffect(() => {
        ensureDeviceRef.current = ensureDevice;
        recoverAfterCallRef.current = () => { teardownDevice(); retryRegistration(); };
    }, [ensureDevice, retryRegistration, teardownDevice]);

    /**
     * Ask for the microphone. Separate from registration so the prompt
     * only appears when a call is actually about to start.
     */
    const requestMicrophone = useCallback(async (): Promise<boolean> => {
        if (micPermission === 'granted') return true;
        if (!window.isSecureContext && window.location.hostname !== 'localhost') {
            setMicPermission('denied');
            setError('Browser calling requires HTTPS so the microphone can be used securely.');
            return false;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            setMicPermission('denied');
            setError('This browser does not support microphone access.');
            return false;
        }
        setMicPermission('requesting');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop());
            setMicPermission('granted');
            setError(null);
            return true;
        } catch (permissionError: unknown) {
            const perm = asError(permissionError);
            const denied = perm.name === 'NotAllowedError' || perm.name === 'PermissionDeniedError';
            setMicPermission(denied ? 'denied' : 'prompt');
            setError(denied
                ? 'Microphone permission was denied. Allow microphone access in your browser settings and try again.'
                : perm.message || 'Could not get microphone permission.');
            return false;
        }
    }, [micPermission]);

    /** Microphone + registered Device — everything a call needs before
     * it can be placed or answered. */
    const ensureCallable = useCallback(async (): Promise<boolean> => {
        setError(null);
        if (!(await requestMicrophone())) return false;
        return Boolean(await ensureDevice());
    }, [ensureDevice, requestMicrophone]);

    const prepare = useCallback(async () => { await ensureCallable(); }, [ensureCallable]);

    // Register as soon as the admin shell mounts, so an inbound call has
    // somewhere to land without anyone opening the dialer first. The
    // cleanup is the only place the Device is destroyed on purpose.
    useEffect(() => {
        unmountedRef.current = false;
        void ensureDeviceRef.current();
        return () => {
            unmountedRef.current = true;
            clearRetryTimer();
            destroyingRef.current = true;
            incomingRef.current?.reject();
            deviceRef.current?.destroy();
            deviceRef.current = null;
            destroyingRef.current = false;
        };
    }, []);

    // Coming back online or back to the tab: a registered browser just
    // refreshes its "seen" time; an offline one retries at once instead
    // of waiting out the backoff.
    useEffect(() => {
        const state = registration.state;
        if (state !== 'registered' && state !== 'offline') return;
        const onReturn = () => {
            if (document.visibilityState !== 'visible') return;
            if (state === 'registered') registerWebDevice();
            else retryRegistration();
        };
        window.addEventListener('online', onReturn);
        document.addEventListener('visibilitychange', onReturn);
        return () => {
            window.removeEventListener('online', onReturn);
            document.removeEventListener('visibilitychange', onReturn);
        };
    }, [registration.state, registerWebDevice, retryRegistration]);

    const attachCallEvents = (call: TwilioCall) => {
        call.on('accept', () => { setStatus('in-call'); setError(null); });
        call.on('disconnect', () => {
            callRef.current = null; setMuted(false); setStatus('ended');
            if (reregisterAfterCallRef.current) {
                reregisterAfterCallRef.current = false;
                recoverAfterCallRef.current();
            }
        });
        call.on('cancel', () => { callRef.current = null; setStatus('ended'); });
        call.on('reject', () => { callRef.current = null; setStatus('ended'); });
        call.on('error', (twilioError: unknown) => {
            setError(getVoiceErrorMessage(twilioError, 'Call failed'));
            callRef.current = null;
            setMuted(false);
            setStatus('ready');
        });
        call.on('mute', (isMuted: boolean) => setMuted(isMuted));
    };

    const connect = useCallback(async (params: Record<string, string>, target: string | null, label: string | null) => {
        const device = deviceRef.current;
        if (!device || callRef.current) return;
        setStatus('connecting');
        setError(null);
        setCallTarget(target);
        setCallLabel(label);
        setMovedTo(null);
        try {
            const call = await device.connect({
                params: { ...params, DeviceId: deviceId, DeviceKind: DEVICE_KIND_WEB, DeviceName: deviceName },
                rtcConstraints: { audio: true },
            });
            callRef.current = call;
            setStatus('ringing');
            attachCallEvents(call);
        } catch (err: unknown) {
            setError(asError(err).message || 'Failed to start browser call. Check microphone permission.');
            setStatus('ready');
            callRef.current = null;
        }
    }, [deviceId, deviceName]);

    // A banned contact is never dialled; the server refuses too, this says why first.
    const callNumber = useCallback(async (phone: string) => {
        const ban = await contactBanApi.forPhone(phone);
        if (ban) {
            setError(ban.message || banSummary(ban));
            return;
        }
        await connect({ To: phone }, phone, null);
    }, [connect]);

    // ── Answering ──
    const acceptIncoming = useCallback(async () => {
        const call = incomingRef.current;
        if (!call) return;
        // The microphone prompt has to come before accept(): accepting
        // without one connects a call the caller can't be heard on.
        if (!(await ensureCallable())) return;
        if (!incomingRef.current) return;  // cancelled while we asked
        const incoming = describeIncoming(call);
        if (!incoming.answerable) return;  // a banned caller under "Ring, but can't answer"
        setCallTarget(incoming.phone);
        setCallLabel(incoming.displayName || incoming.phone);
        setMovedTo(null);
        clearIncoming();
        callRef.current = call;
        attachCallEvents(call);
        setStatus('in-call');
        call.accept();
    }, [clearIncoming, ensureCallable]);

    const rejectIncoming = useCallback(() => {
        incomingRef.current?.reject();
        clearIncoming();
    }, [clearIncoming]);

    const joinConference = useCallback(
        (conferenceName: string, label?: string | null) =>
            connect({ ConferenceName: conferenceName }, null, label ?? null),
        [connect],
    );

    const hangUp = useCallback(() => {
        callRef.current?.disconnect();
        deviceRef.current?.disconnectAll();
        callRef.current = null;
        setStatus(deviceRef.current ? 'ready' : 'idle');
    }, []);

    const toggleMute = useCallback(() => {
        const call = callRef.current;
        if (!call) return;
        call.mute(!muted);
        setMuted(!muted);
    }, [muted]);

    // ── Cross-device registry via WebSocket ──
    const handleCallActive = useCallback((raw: WsEvent['data']) => {
        setActiveCall(parseActiveCall(raw as Record<string, unknown>));
    }, []);

    const handleCallHandoff = useCallback((raw: WsEvent['data']) => {
        const data = raw as { to_device?: Record<string, string>; from_device?: Record<string, string> };
        const to = data.to_device || {};
        const from = data.from_device || {};
        // Our leg was just replaced by another device → the SDK will fire
        // `disconnect`; remember why so the UI says "Moved to iPhone".
        if (from.device_id === deviceId && to.device_id !== deviceId) {
            setMovedTo(to.device_name || to.device_kind || 'another device');
        }
        setInvite(null);
    }, [deviceId]);

    const handleInvite = useCallback((raw: WsEvent['data']) => {
        const data = raw as Partial<HandoffInvite>;
        if (data.to_device_id !== deviceId || !data.conference_name) return;
        setInvite({
            conference_name: String(data.conference_name),
            phone: data.phone || null,
            display_name: data.display_name || null,
            to_device_id: String(data.to_device_id),
            from_device_name: data.from_device_name || null,
        });
    }, [deviceId]);

    const upsertFollowup = useCallback((raw: WsEvent['data']) => {
        const row = parseFollowup(raw as Record<string, unknown>);
        if (!row) return;
        setFollowups(prev => {
            const others = prev.filter(f => f.id !== row.id);
            return isFollowupOpen(row) ? [...others, row] : others;
        });
    }, []);

    useSmsWebSocket({
        onCallActive: handleCallActive,
        onCallHandoff: handleCallHandoff,
        onCallHandoffInvite: handleInvite,
        onFollowupScheduled: upsertFollowup,
        onFollowupSent: upsertFollowup,
        onFollowupCancelled: upsertFollowup,
    });

    const isCalling = status === 'connecting' || status === 'ringing' || status === 'in-call';
    const isActiveElsewhere = Boolean(activeCall && activeCall.device_id !== deviceId && !isCalling);

    const continueHere = useCallback(async () => {
        if (!activeCall) return;
        if (!(await ensureCallable())) return;
        await joinConference(activeCall.conference_name, activeCall.display_name || activeCall.phone);
    }, [activeCall, ensureCallable, joinConference]);

    const endActiveCall = useCallback(async () => {
        await apiClient.post('/voice/calls/active/end');
        setActiveCall(null);
    }, []);

    const acceptInvite = useCallback(async () => {
        if (!invite) return;
        const pending = invite;
        setInvite(null);
        if (!(await ensureCallable())) return;
        await joinConference(pending.conference_name, pending.display_name || pending.phone);
    }, [ensureCallable, invite, joinConference]);

    const dismissInvite = useCallback(() => setInvite(null), []);

    const sendToDevice = useCallback(async (toDeviceId: string) => {
        await apiClient.post('/voice/calls/active/invite', { to_device_id: toDeviceId, from_device_name: deviceName });
    }, [deviceName]);

    const cancelFollowup = useCallback(async (id: number) => {
        const res = await apiClient.post(`/call-followup/${id}/cancel`);
        if (res.data?.data) upsertFollowup(res.data.data);
    }, [upsertFollowup]);

    const sendFollowupNow = useCallback(async (id: number) => {
        const res = await apiClient.post(`/call-followup/${id}/send-now`);
        if (res.data?.data) upsertFollowup(res.data.data);
    }, [upsertFollowup]);

    const value: VoiceContextValue = {
        status, micPermission, initializing, error,
        // "Ready to place a call" — registration alone isn't enough,
        // a call still needs the microphone.
        isReady: status !== 'idle' && hasDevice && micPermission === 'granted',
        isCalling, callTarget, callLabel, muted, isRegistered,
        registration, retryRegistration,
        prepare, callNumber, joinConference, hangUp, toggleMute,
        incomingCall, acceptIncoming, rejectIncoming,
        deviceId, activeCall, isActiveElsewhere, continueHere, endActiveCall,
        invite, acceptInvite, dismissInvite, sendToDevice,
        movedTo, clearMovedTo: () => setMovedTo(null),
        followups, cancelFollowup, sendFollowupNow,
    };

    return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export function useVoice(): VoiceContextValue {
    const ctx = useContext(VoiceContext);
    if (!ctx) throw new Error('useVoice must be used within VoiceProvider');
    return ctx;
}
